/**
 * Ein Servicepunkt – anlegen oder bearbeiten, dasselbe Formular.
 *
 * Pflicht: Maßnahme, Verantwortlicher, Termin oder Zeitfenster. Dieselbe Regel
 * wie bei Bestellungen: nichts darf „offen ohne Datum" sein. Leere Pflichtfelder
 * sind rot umrandet, der Satz darunter sagt, was fehlt, der Knopf bleibt
 * gesperrt.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer, Pflicht, PflichtLegende } from '../komponenten/Modal';
import type { Angebot, AngebotAntwort, Servicefall, Servicepunkt } from '../typen';

/** Die Felder eines Punkts – vom Servicefall-Dialog wiederverwendet. */
export function ServicepunktFelder({
  werte,
  setWerte,
  kalkulant,
  rot,
}: {
  werte: PunktWerte;
  setWerte: (w: PunktWerte) => void;
  kalkulant: string;
  rot: (feld: string) => string;
}) {
  const meta = useMeta();
  return (
    <>
      <label className="modal-feld">
        <span>Maßnahme<Pflicht /></span>
        <input
          className={`modal-eingabe ${rot('Maßnahme')}`}
          value={werte.massnahme}
          placeholder="z. B. Ersatzmotor bestellen und tauschen"
          onChange={(e) => setWerte({ ...werte, massnahme: e.target.value })}
        />
      </label>
      <label className="modal-feld">
        <span>Bezug · optional</span>
        <input
          className="modal-eingabe"
          value={werte.bezug}
          placeholder="Ersatzteil, Ort, Rückfrage – z. B. Motor io bei Somfy"
          onChange={(e) => setWerte({ ...werte, bezug: e.target.value })}
        />
      </label>
      <label className="modal-feld">
        <span>Verantwortlich<Pflicht /></span>
        <select
          className={`modal-eingabe ${rot('Verantwortlicher')}`}
          value={werte.verantwortlich}
          onChange={(e) => setWerte({ ...werte, verantwortlich: e.target.value })}
        >
          <option value="">bitte wählen …</option>
          {meta.team.map((t) => (
            <option key={t.name} value={t.name}>
              {t.anzeigename || t.name}
              {t.name === kalkulant ? ' · Kalkulant' : ''}
            </option>
          ))}
        </select>
      </label>
      <div className="modal-feld">
        <span>Termin oder Zeitfenster<Pflicht /></span>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label className="modal-feld" style={{ flex: '0 0 170px', marginTop: 0 }}>
            <span style={{ fontSize: 12 }}>Datum</span>
            <input
              type="date"
              className={`modal-eingabe ${rot('Termin')}`}
              value={werte.termin}
              onChange={(e) => setWerte({ ...werte, termin: e.target.value })}
            />
          </label>
          <label className="modal-feld" style={{ flex: 1, minWidth: 180, marginTop: 0 }}>
            <span style={{ fontSize: 12 }}>Zeitfenster</span>
            <input
              className={`modal-eingabe ${rot('Termin')}`}
              value={werte.zeitfenster}
              placeholder="z. B. KW 40 · nach Lieferung des Ersatzteils"
              onChange={(e) => setWerte({ ...werte, zeitfenster: e.target.value })}
            />
          </label>
        </div>
      </div>
      <label className="modal-feld">
        <span>Notiz · optional</span>
        <textarea
          className="modal-textarea"
          rows={2}
          value={werte.notiz}
          onChange={(e) => setWerte({ ...werte, notiz: e.target.value })}
        />
      </label>
    </>
  );
}

export interface PunktWerte {
  massnahme: string;
  bezug: string;
  notiz: string;
  termin: string;
  zeitfenster: string;
  verantwortlich: string;
}

export function punktFehlt(w: PunktWerte): string[] {
  const fehlt: string[] = [];
  if (!w.massnahme.trim()) fehlt.push('Maßnahme');
  if (!w.verantwortlich) fehlt.push('Verantwortlicher');
  if (!w.termin && !w.zeitfenster.trim()) fehlt.push('Termin');
  return fehlt;
}

export function punktKoerper(w: PunktWerte) {
  return {
    massnahme: w.massnahme.trim(),
    bezug: w.bezug.trim(),
    notiz: w.notiz.trim(),
    termin: w.termin || null,
    zeitfenster: w.zeitfenster.trim(),
    verantwortlich: w.verantwortlich,
  };
}

export function ServicepunktModal({
  fall,
  punkt,
  angebot,
  onSchliessen,
  onFertig,
}: {
  fall: Servicefall;
  /** null: neuen Punkt anlegen. */
  punkt: Servicepunkt | null;
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const neu = punkt === null;
  const [werte, setWerte] = useState<PunktWerte>({
    massnahme: punkt?.massnahme ?? '',
    bezug: punkt?.bezug ?? '',
    notiz: punkt?.notiz ?? '',
    termin: punkt?.termin ?? '',
    zeitfenster: punkt?.zeitfenster ?? '',
    verantwortlich: punkt?.verantwortlich ?? angebot.kalkulant,
  });
  const [fehler, setFehler] = useState('');
  const fehlt = punktFehlt(werte);
  const rot = (feld: string) => (fehlt.includes(feld) ? 'modal-eingabe--fehler' : '');

  const mutation = useMutation({
    mutationFn: () =>
      neu
        ? api.sende<AngebotAntwort>(`/service/${fall.id}/punkte`, punktKoerper(werte))
        : api.aendere<AngebotAntwort>(`/service/punkte/${punkt.id}`, punktKoerper(werte)),
    onSuccess: (a) => {
      onFertig();
      toastZeigen(a.toast ?? 'Gespeichert.');
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  return (
    <Modal
      titel={neu ? 'Servicepunkt hinzufügen' : 'Servicepunkt bearbeiten'}
      untertitel={`${fall.kennung} · ${fall.titel}`}
      breite={540}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={() => mutation.mutate()} disabled={mutation.isPending || fehlt.length > 0}>
            {mutation.isPending ? 'Wird gespeichert …' : neu ? 'Punkt hinzufügen' : 'Speichern'}
          </KnopfPrimaer>
        </>
      }
    >
      <ServicepunktFelder
        werte={werte}
        setWerte={(w) => {
          setWerte(w);
          setFehler('');
        }}
        kalkulant={angebot.kalkulant}
        rot={rot}
      />
      {fehlt.length > 0 && (
        <div className="modal-hinweis" style={{ color: 'var(--eh-rot)', fontWeight: 600 }}>
          Es fehlt noch: {fehlt.join(', ')}.
        </div>
      )}
      <PflichtLegende />
      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
